import { ReactElement } from 'react'
import { useTheme } from '@features/theme/theme.hooks'
import { useNeopaymentListUserSubscriptionsQuery } from '@app/services/auto-generated/neopayment'
import { Box, Button, Divider, Paper, Stack } from '@common/components'
import { CheckCircleIcon } from '@common/icons'
import { Typography } from './card-pricing.styles'

const CardPricing = (props: {
  popular?: boolean
  highlighted?: boolean
  features: (string | ReactElement)[]
  plan: string
  price: string
  description: string
  discount?: string
  originalPrice?: string
  callToAction?: () => JSX.Element
  isCurrentPlan?: boolean
  plan_id?: string
}) => {
  const { theme } = useTheme()
  const { data: userSubscriptions } = useNeopaymentListUserSubscriptionsQuery()
  const currentSubscription = userSubscriptions?.subscriptions[0]

  const getDaysLeft = () => {
    if (!currentSubscription?.end_date || !currentSubscription?.cancel_at_period_end) return null
    const endDate = new Date(currentSubscription.end_date)
    const today = new Date()
    const diffTime = Math.abs(endDate.getTime() - today.getTime())
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
    return diffDays
  }

  const daysLeft = getDaysLeft()
  const subscriptionStatus = currentSubscription?.cancel_at_period_end
    ? `${daysLeft} days left`
    : 'Subscribed'

  return (
    <Paper
      sx={{
        maxWidth: '350px',
        mx: 'auto',
        boxShadow: props.isCurrentPlan ? '0 0 0 2px #3787FF' : props.highlighted ? 1 : 0,
        height: '100%',
        position: 'relative',
        px: 4,
        py: 6,
        background: props.highlighted
          ? 'radial-gradient(164.75% 100% at 50% 0%, #334155 0%, #0f172a 48.73%)'
          : '',
        overflow: 'hidden',
      }}
    >
      {props.isCurrentPlan && props.plan_id !== 'free' && (
        <>
          <Box
            sx={{
              position: 'absolute',
              top: 14,
              left: -40,
              transform: 'rotate(-35deg)',
              backgroundColor: theme.palette.mode === 'dark' ? '#3787FF' : '#2563eb',
              width: '170px',
              height: '28px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
            }}
          >
            <Typography
              sx={{
                color: 'white',
                fontSize: '0.65rem',
                fontWeight: 600,
                letterSpacing: '0.5px',
                textTransform: 'uppercase',
                lineHeight: 1,
                textShadow: '0 1px 2px rgba(0,0,0,0.2)',
              }}
            >
              {subscriptionStatus}
            </Typography>
          </Box>
        </>
      )}

      {props.popular && (
        <Typography
          sx={{
            position: 'absolute',
            top: '14px',
            right: '14px',
            textTransform: 'uppercase',
            color: '#3787FF',
            backgroundColor:
              theme.palette.mode === 'dark' ? 'rgba(55, 135, 255, 0.1)' : 'rgba(55, 135, 255, 0.1)',
            padding: '4px 12px',
            border: '1.5px solid #3787FF',
            fontWeight: 600,
            borderRadius: '30px',
            fontSize: '0.75rem',
            letterSpacing: '0.5px',
          }}
        >
          Popular
        </Typography>
      )}

      <Stack spacing={2} sx={{}}>
        <Typography
          variant='h5'
          fontWeight={600}
          sx={{ color: props.highlighted ? 'white' : 'inherit' }}
        >
          {props.plan}
        </Typography>
        <Stack direction={'row'} alignItems={'center'} sx={{ height: '55px' }}>
          <Typography
            variant='h5'
            fontWeight={600}
            sx={{ color: props.highlighted ? 'white' : 'inherit' }}
          >
            {props.price}
            <span style={{ fontWeight: 500, fontSize: '12px', opacity: 0.8 }}> / month</span>
          </Typography>
          {props.originalPrice && props.discount && (
            <Typography
              fontWeight={600}
              sx={{ ml: 2, color: props.highlighted ? 'white' : 'inherit' }}
            >
              <span style={{ fontWeight: 500, textDecorationLine: 'line-through', opacity: 0.8 }}>
                {props.originalPrice}
              </span>{' '}
              {/* <br /> */}
              {props.discount}
            </Typography>
          )}
        </Stack>
        <Stack>
          {props.isCurrentPlan ? (
            <Button
              color={'neutral'}
              variant={'contained'}
              // disabled
              sx={{
                pointerEvents: 'none',
                opacity: 0.5,
                mb: 3,
                alignSelf: 'center',
                width: '180px',
                fontWeight: 'bold',
              }}
            >
              Current Plan
            </Button>
          ) : (
            props.callToAction && <props.callToAction />
          )}
          <Typography
            fontWeight={600}
            sx={{
              color: (theme) =>
                theme.palette.mode === 'dark'
                  ? props.highlighted
                    ? '#D1D5DB'
                    : '#E5E5E5'
                  : props.highlighted
                    ? '#D1D5DB'
                    : '#4B5563',
            }}
          >
            {props.description}
          </Typography>
        </Stack>
        <Stack component='ul' spacing={1} sx={{ listStyleType: 'none', p: 0, pt: 1 }}>
          {props.features?.map((feature, index) => (
            <>
              <Stack key={index} direction={'row'} alignItems={'center'} spacing={1} component='li'>
                <CheckCircleIcon color='primary' />
                <Typography
                  color='text.secondary'
                  sx={{
                    color: (theme) =>
                      theme.palette.mode === 'dark'
                        ? props.highlighted
                          ? '#D1D5DB'
                          : '#E5E5E5'
                        : props.highlighted
                          ? '#D1D5DB'
                          : '#4B5563',
                  }}
                >
                  {feature}
                </Typography>
              </Stack>
              {index !== props.features.length - 1 && <Divider />}
            </>
          ))}
        </Stack>
      </Stack>
    </Paper>
  )
}

export default CardPricing
